import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { toSnakeCase } from '@quickbit/backoffice-core-frontend/src/api/fetchAPI';
import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

export interface FrozenAsset {
  transactionId: string;
  reason: string;
  amount: string;
  currency: string;
  date: string;
}

export interface SearchFrozenAssetsFilters {
  transactionId?: string;
  reason?: string;
  createdFrom?: string;
  createdTo?: string;
}

export interface SearchFrozenAssetsResponse {
  aggregatedData: {
    count: number;
  };
  frozenAssets: FrozenAsset[];
}

const queryKeys = {
  all: () => ['frozenAssets'] as const,
  getFrozenAssets: (
    filters: SearchFrozenAssetsFilters,
    pagination: { size: number; page: number }
  ) => [...queryKeys.all(), 'getFrozenAssets', filters, pagination] as const,
};

export const useFrozenAssetsApi = () => {
  const useGetFrozenAssets = (
    filters: SearchFrozenAssetsFilters,
    pagination: { size: number; page: number }
  ): UseQueryResult<SearchFrozenAssetsResponse> => {
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(toSnakeCase({ ...filters, ...pagination }))
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)])
      )
    ).toString();
    return useQuery({
      queryKey: queryKeys.getFrozenAssets(filters, pagination),
      queryFn: async () =>
        await fetchAPI<SearchFrozenAssetsResponse>(
          `v1/frozen-assets?${queryString}`,
          {
            method: 'GET',
          }
        ),
      enabled: true,
      staleTime: 1000 * 60 * 1,
      placeholderData: keepPreviousData,
    });
  };

  return {
    useGetFrozenAssets,
  };
};
